/**
 * menu-cart.js
 *
 * Pickup/delivery-date enforced Add to Cart for weekly menu collections.
 *
 * Responsibilities:
 *   1. isWithinDefaultWindow() — determines whether the default ordering
 *      window (Thu 6 PM CT → Mon 9 AM CT) is currently open.
 *   2. enforceDefaultWindow()  — enables/disables [data-state="default-window"]
 *      buttons on page load based on (1).
 *   3. handleAddToCartClick()  — intercepts button clicks, enforces pickup/delivery-date
 *      consistency across cart line items, and posts to /cart/add.js.
 *
 * Rules:
 *   - No jQuery. No deprecated Shopify JS APIs.
 *   - fetch API only.
 *   - Network failure on /cart.js must BLOCK the add (fail safe).
 *   - Errors shown inline, adjacent to the button. No alert() or confirm().
 *   - Pickup/delivery date read from button.dataset.pickupDay at click time —
 *     never from the page URL or global state.
 */

(function () {
  'use strict';

  // ─── 1. isWithinDefaultWindow ───────────────────────────────────────────────
  //
  // Default ordering window: Thursday 6:00 PM CT → Monday 9:00 AM CT
  //
  // TIMEZONE APPROACH
  // Central Time observes two UTC offsets:
  //   CDT (Central Daylight Time) = UTC−5  (second Sun Mar → first Sun Nov)
  //   CST (Central Standard Time) = UTC−6  (first Sun Nov → second Sun Mar)
  //
  // UTC equivalents — shown here for verification, NOT used in code:
  //   CDT:  opens Thu 23:00 UTC,  closes Mon 14:00 UTC
  //   CST:  opens Fri 00:00 UTC,  closes Mon 15:00 UTC
  //
  // WHY WE CANNOT HARDCODE A SINGLE UTC OFFSET:
  //   Using UTC−5 is wrong for ~5 months/year (CST period).
  //   Using UTC−6 is wrong for ~7 months/year (CDT period).
  //   A hardcoded offset would silently miscompute the window twice per year
  //   on DST transition weekends — exactly the kind of bug that looks fine in
  //   testing and surfaces on the worst possible Sunday.
  //
  // SOLUTION: Intl.DateTimeFormat with timeZone: 'America/Chicago'
  //   The browser's IANA timezone database handles DST automatically.
  //   formatToParts() extracts the weekday and hour:minute in CT wall-clock
  //   time; we apply the Thu 18:00 → Mon 09:00 rule directly.
  //
  // KNOWN LIMITATION:
  //   - This check runs once at page load. A customer who leaves the page open
  //     across the Thu 6 PM open (or Mon 9 AM close) will see stale state until
  //     they refresh. Add a setTimeout targeting the next transition if needed.
  //   - IE11 does not support Intl with the full IANA tz database. Shopify
  //     dropped IE11 support in 2022; this is an accepted tradeoff.
  //   - For server-side DST-correct logic in Liquid, a timezone library would
  //     be required. Liquid alone cannot solve this cleanly.

  function isWithinDefaultWindow() {
    var now = new Date();

    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      weekday:  'short',   // 'Sun' 'Mon' 'Tue' 'Wed' 'Thu' 'Fri' 'Sat'
      hour:     '2-digit', // '00'–'23' with hour12: false
      minute:   '2-digit',
      hour12:   false,
    });

    // Build { type: value } map from formatToParts.
    // Using 'en-US' locale explicitly so weekday abbreviations are always
    // English regardless of the visitor's browser locale setting.
    var parts = {};
    fmt.formatToParts(now).forEach(function (p) {
      parts[p.type] = p.value;
    });

    // Normalise hour: some implementations return '24' at midnight rather
    // than '00'. Modulo 24 collapses both to 0.
    var hour   = parseInt(parts.hour,   10) % 24;
    var minute = parseInt(parts.minute, 10);
    var minutesFromMidnight = hour * 60 + minute;

    var OPEN_MIN  = 18 * 60; // 18:00 CT Thursday
    var CLOSE_MIN =  9 * 60; //  9:00 CT Monday

    switch (parts.weekday) {
      case 'Thu': return minutesFromMidnight >= OPEN_MIN;
      case 'Fri': return true;
      case 'Sat': return true;
      case 'Sun': return true;
      case 'Mon': return minutesFromMidnight < CLOSE_MIN;
      default:    return false; // Tue, Wed
    }
  }


  // ─── Pickup date formatting ─────────────────────────────────────────────────
  //
  // Computes a human-readable "Day, Month Date" string for the pickup/delivery date.
  // Uses the menu_date metafield (e.g. "March 31, 2026") to anchor the week,
  // then finds the matching weekday within that week.
  //
  // Fallback: if menu_date is blank or unparseable, returns the capitalized
  // day name only (e.g. "Tuesday") — the existing behaviour.
  //
  // Special case: pickupDay === "event" (from special events) returns "Event".

  function formatPickupDate(pickupDay, menuDate) {
    if (!pickupDay) return '';

    // Special events — pass through the menu date as the display value
    if (pickupDay.toLowerCase() === 'event') {
      return menuDate || 'Special Event';
    }

    var dayCapitalized = pickupDay.charAt(0).toUpperCase() + pickupDay.slice(1);

    if (!menuDate) return dayCapitalized;

    // Parse the menu_date string. Shopify menu_date metafield is free-text,
    // typically "March 31, 2026" or "March 31" or similar.
    var parsed = new Date(menuDate);
    if (isNaN(parsed.getTime())) return dayCapitalized;

    // Map day name to JS getDay() index (0=Sun, 1=Mon, ..., 6=Sat)
    var dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    };
    var targetDayIndex = dayMap[pickupDay.toLowerCase()];
    if (targetDayIndex === undefined) return dayCapitalized;

    // Find the target weekday within ±6 days of the parsed menu_date.
    // This handles both "menu date IS the pickup/delivery date" and "menu date is
    // the week-of date with pickup on a different day".
    var currentDayIndex = parsed.getDay();
    var diff = targetDayIndex - currentDayIndex;
    if (diff < -3) diff += 7; // prefer forward direction
    if (diff > 3) diff -= 7;  // but allow a few days back

    var targetDate = new Date(parsed);
    targetDate.setDate(targetDate.getDate() + diff);

    // Format: "Tuesday, March 31"
    var months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var days = [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday',
      'Thursday', 'Friday', 'Saturday'
    ];

    return days[targetDate.getDay()] + ', ' +
           months[targetDate.getMonth()] + ' ' +
           targetDate.getDate();
  }


  // ─── Error display helpers ──────────────────────────────────────────────────

  function showError(button, message) {
    // The Liquid snippet renders a <p class="menu-col-card__error" style="display: block; font-size:0.8rem; color:#8E4585;" hidden>
    // sibling for every Add to Cart button. Find it; fall back to creating one.
    var errorEl = button.parentElement
      ? button.parentElement.querySelector('.menu-col-card__error')
      : null;

    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'menu-col-card__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('style', 'display: block; font-size:0.9rem; color:#800020;')
      errorEl.setAttribute('aria-live', 'polite');
      button.insertAdjacentElement('afterend', errorEl);
    }

    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');
  }

  function clearError(button) {
    var errorEl = button.parentElement
      ? button.parentElement.querySelector('.menu-col-card__error')
      : null;
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }


  // ─── Cart helpers ───────────────────────────────────────────────────────────

  /**
   * Fetches /cart.js and returns { pickupDay, collectionHandle } from the
   * first line item that carries _pickup_day, or null if the cart is empty
   * or no item has the property.
   *
   * collectionHandle may be '' if the item predates the _collection_handle
   * property — callers must handle that gracefully.
   *
   * THROWS on network failure or non-2xx HTTP status.
   * Callers MUST handle the rejection — do not swallow it.
   */
  function getCartPickupDay() {
    return fetch('/cart.js', {
      method:  'GET',
      headers: { 'Accept': 'application/json' },
    })
    .then(function (res) {
      if (!res.ok) {
        throw new Error('Cart fetch failed (HTTP ' + res.status + ')');
      }
      return res.json();
    })
    .then(function (cart) {
      if (!cart.items || cart.items.length === 0) {
        return null; // empty cart — no pickup/delivery-date constraint
      }
      for (var i = 0; i < cart.items.length; i++) {
        var props = cart.items[i].properties;
        if (props && props._pickup_day) {
          return {
            pickupDay:        props._pickup_day,
            collectionHandle: props._collection_handle || '',
          };
        }
      }
      return null; // items present but none have _pickup_day (edge case)
    });
  }

  /**
   * POSTs to /cart/add.js. Resolves with the parsed response on success.
   * THROWS with a descriptive message on HTTP error or network failure.
   */
  function postAddToCart(variantId, quantity, pickupDay, menuDate, collectionHandle, orderClose, availablePickupDates, menuTitle) {
    // _pickup_day         — underscore prefix hides from customer storefront; used for
    //                       pickup/delivery-date enforcement across cart line items.
    // _collection_handle  — hidden; identifies source collection for traceability.
    // _order_close        — hidden; ISO datetime of ordering window close; used by
    //                       cart page auto-removal of expired items.
    // Pickup/Delivery Date — visible to customer on checkout, order confirmation email,
    //                       order status (thank you) page, and packing slip.
    // Menu                — visible; "Weeknight Meals" or the special event name.
    //
    // Showing both _pickup_day and Pickup/Delivery Date is intentional: one is for
    // programmatic enforcement, the other is for human-readable display.
    var properties = {
      _pickup_day:  pickupDay,
      'Pickup/Delivery Date': formatPickupDate(pickupDay, menuDate),
    };
    if (collectionHandle) {
      properties._collection_handle = collectionHandle;
    }
    if (orderClose) {
      properties._order_close = orderClose;
    }
    if (menuTitle) {
      properties['Menu'] = menuTitle;
    }
    if (pickupDay && pickupDay.toLowerCase() === 'event' && availablePickupDates) {
      properties._available_pickup_dates = availablePickupDates;
      var firstDate = availablePickupDates.split('|')[0];
      if (firstDate) {
        properties['Pickup/Delivery Date'] = firstDate;
      }
    }

    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id:         Number(variantId),
        quantity:   quantity,
        properties: properties,
      }),
    })
    .then(function (res) {
      if (!res.ok) {
        // Try to surface Shopify's error description; fall back to HTTP status.
        return res.json()
          .then(function (data) {
            throw new Error(
              data.description || ('Add to cart failed (HTTP ' + res.status + ')')
            );
          })
          .catch(function (parseErr) {
            // If res.json() itself throws (non-JSON body), re-throw the status error.
            if (parseErr.message && parseErr.message.indexOf('Add to cart failed') === 0) {
              throw parseErr;
            }
            throw new Error('Add to cart failed (HTTP ' + res.status + ')');
          });
      }
      return res.json();
    });
    // Network failure (TypeError) propagates to the caller's catch.
  }


  // ─── Main click handler ─────────────────────────────────────────────────────

  function handleAddToCartClick(button) {
    var pickupDay        = button.dataset.pickupDay;
    var variantId        = button.dataset.variantId;

    // Defense-in-depth guard against a stale data-variant-id.
    // If a <select> sits alongside this button in the same parent container
    // (the card body), trust its current value over the button's attribute.
    // This protects against the rare case where the section-level change
    // handler fails to propagate the selection to data-variant-id — a
    // silent failure that would otherwise add the wrong variant to cart.
    var container = button.parentElement;
    var variantSelect = container ? container.querySelector('select') : null;
    if (variantSelect && variantSelect.value) {
      variantId = variantSelect.value;
    }

    var menuDate         = button.dataset.menuDate || '';
    var collectionHandle      = button.dataset.collectionHandle || '';
    var orderClose            = button.dataset.orderClose || '';
    var availablePickupDates  = button.dataset.availablePickupDates || '';
    var menuTitle             = button.dataset.menuTitle || '';
    var quantity              = 1;

    if (!pickupDay || !variantId) {
      console.error(
        '[menu-cart] Button is missing data-pickup-day or data-variant-id:',
        button
      );
      return;
    }

    clearError(button);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    // Step 1: fetch the cart to check the existing pickup/delivery date.
    //
    // FAIL SAFE: if this fetch fails for any reason (network down, server error),
    // we surface an error and DO NOT proceed with the add. An unknown cart state
    // must not silently allow a potentially mismatched item to be added.
    getCartPickupDay()
      .then(function (existing) {

        // Step 2: pickup/delivery-date consistency check.
        // Block when pickup day differs OR when the same weekday comes from
        // a different collection (cross-week conflict). If the existing item
        // lacks _collection_handle (legacy), fall back to day-only check.
        if (existing !== null) {
          var dayMismatch = existing.pickupDay !== pickupDay;
          var crossWeek   = !dayMismatch
                            && existing.collectionHandle !== ''
                            && collectionHandle !== ''
                            && existing.collectionHandle !== collectionHandle;

          if (dayMismatch || crossWeek) {
            var displayName = function (s) {
              if (s.toLowerCase() === 'event') return 'Special Event';
              return s.charAt(0).toUpperCase() + s.slice(1);
            };
            showError(
              button,
              'Your cart contains a ' + displayName(existing.pickupDay) + ' pickup/delivery item. ' +
              'This item is for ' + displayName(pickupDay) + ' pickup/delivery. ' +
              'Please complete or clear your current order before adding ' +
              'items from a different pickup/delivery date.'
            );
            return null; // sentinel value — skip step 3
          }
        }

        // Step 3: add to cart.
        return postAddToCart(variantId, quantity, pickupDay, menuDate, collectionHandle, orderClose, availablePickupDates, menuTitle);
      })
      .then(function (result) {
        if (result === null) return; // mismatch already handled above

        // Dispatch a custom event so the theme can open a cart drawer,
        // update a cart count badge, etc.
        document.dispatchEvent(new CustomEvent('menu:cart:itemAdded', {
          detail:  { variantId: variantId, pickupDay: pickupDay },
          bubbles: true,
        }));

        // ── Dawn cart bubble refresh ────────────────────────────────
        // Dawn listens for cart updates via multiple mechanisms.
        // Dispatch events and re-render the cart-icon-bubble section
        // so the header count stays in sync after add-to-cart.
        fetch('/cart.js', { method: 'GET', headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { cart: cart }
            }));
          });
        document.dispatchEvent(new CustomEvent('cart-update'));

        var cartBubbleEl = document.getElementById('cart-icon-bubble');
        if (cartBubbleEl) {
          fetch('/?sections=cart-icon-bubble')
            .then(function (res) { return res.json(); })
            .then(function (sections) {
              if (sections['cart-icon-bubble']) {
                var parser = new DOMParser();
                var doc = parser.parseFromString(
                  sections['cart-icon-bubble'], 'text/html'
                );
                var newBubble = doc.querySelector('.cart-count-bubble');
                var currentBubble = cartBubbleEl.querySelector('.cart-count-bubble');
                if (newBubble && currentBubble) {
                  currentBubble.outerHTML = newBubble.outerHTML;
                } else if (newBubble && !currentBubble) {
                  // Cart was empty (no bubble existed), insert the new one
                  cartBubbleEl.appendChild(newBubble);
                }
              }
            });
        }
        // ── End Dawn cart bubble refresh ─────────────────────────────

        // If the button carries data-redirect-after-add, navigate there now.
        // This lets the merchant send customers directly to /cart for checkout
        // without needing a cart drawer integration. Remove the data attribute
        // on the button (in menu-col-card.liquid) to use a drawer instead.
        var redirect = button.dataset.redirectAfterAdd;
        if (redirect) {
          window.location.href = redirect;
          return; // skip animation — page is navigating away
        }

        // No redirect: brief visual feedback on the button.
        var originalText = button.textContent;
        button.textContent = 'Added!';
        setTimeout(function () {
          button.textContent = originalText;
        }, 1500);
      })
      .catch(function (err) {
        // Covers: cart fetch failure, /cart/add.js HTTP errors, network failure.
        var message = (err && err.message) ? err.message : '';

        if (message.indexOf('Cart fetch failed') === 0) {
          // Be explicit that we blocked the add — don't let the user assume
          // the item was added.
          message = 'Unable to verify your cart. Please refresh the page and try again.';
        } else if (!message) {
          message = 'Could not add to cart. Please try again.';
        }

        showError(button, message);
      })
      .finally(function () {
        // Always restore the button — whether add succeeded, failed, or was
        // blocked by a mismatch. The button remains clickable so the user can
        // retry or read the inline error.
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }


  // ─── 2. Default-window enforcement ─────────────────────────────────────────
  //
  // Runs once on page load. Finds all buttons with data-state="default-window"
  // (collections that have no metafield order_open / order_close override),
  // calls isWithinDefaultWindow(), and enables or disables them accordingly.
  //
  // Also updates the collection card's state label text and CSS modifier class
  // so the visual treatment matches the JS-determined state.

  function enforceDefaultWindow() {
    var buttons = document.querySelectorAll(
      '[data-pickup-day][data-state="default-window"]'
    );
    if (!buttons.length) return;

    var isOpen = isWithinDefaultWindow();

    buttons.forEach(function (button) {
      var card       = button.closest('.menu-col-card');
      var stateLabel = card && card.querySelector('.menu-col-card__state-label');

      if (isOpen) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');

        if (stateLabel) {
          stateLabel.textContent = 'Ordering Open';
          stateLabel.classList.remove(
            'menu-col-card__state-label--default-window'
          );
          stateLabel.classList.add(
            'menu-col-card__state-label--active'
          );
        }

      } else {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');

        if (stateLabel) {
          stateLabel.textContent = 'Ordering Closed';
          stateLabel.classList.remove(
            'menu-col-card__state-label--default-window'
          );
          stateLabel.classList.add(
            'menu-col-card__state-label--closed'
          );
        }

        // Show inline message so the user knows when to return.
        showError(
          button,
          'Ordering is currently closed. Opens Thursday at 6\u202fPM CT.'
          // \u202f = narrow no-break space between the number and "PM"
        );
      }
    });
  }


  // ─── Event delegation ───────────────────────────────────────────────────────
  //
  // Single listener on the document catches clicks on any [data-pickup-day]
  // button — including ones rendered after this script runs (e.g. via a
  // section render call). Uses .closest() to handle clicks on child elements
  // (e.g. text node or icon inside the button).

  document.addEventListener('click', function (event) {
    // Bail early if .closest is not available (very old browsers).
    if (typeof event.target.closest !== 'function') return;

    var button = event.target.closest('[data-pickup-day]');
    if (!button) return;

    // Disabled buttons (closed window, sold out, mid-request) do nothing.
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    handleAddToCartClick(button);
  });


  // ─── Cart page expiry enforcement (two-way) ───────────────────────────────
  //
  // Liquid sets the checkout button's `disabled` attribute at initial page
  // render based on `cart == empty or gf_has_expired_items`. On subsequent
  // cart mutations, however, Dawn's cart.js only re-renders the inner HTML
  // of `.js-contents` inside `#main-cart-footer` — and the checkout button
  // lives in a sibling `.cart__ctas` div, OUTSIDE that selector. So Liquid's
  // updated button markup is thrown away on every remove/update action.
  //
  // This function is the authoritative source of truth for the button's
  // disabled state AFTER initial load. It reads the current DOM:
  //   1. If #main-cart-footer has `is-empty` class (Dawn toggles it on
  //      mutation) → cart is empty → disable.
  //   2. Otherwise, scan all [data-order-close] attributes from line items
  //      (rendered by main-cart-items.liquid) and compare to Date.now(). If
  //      any are in the past → disable. If none → enable.
  //
  // Runs on page load AND on every cart mutation via MutationObserver (see
  // observeCartMutations below).
  function enforceCartExpiry() {
    var checkoutBtn = document.getElementById('checkout');
    if (!checkoutBtn) return; // not on the cart page

    var cartFooter = document.getElementById('main-cart-footer');
    var isEmpty = cartFooter && cartFooter.classList.contains('is-empty');

    if (isEmpty) {
      checkoutBtn.setAttribute('disabled', 'disabled');
      return;
    }

    var now = Date.now();
    var rows = document.querySelectorAll('[data-order-close]');
    var hasExpired = false;
    for (var i = 0; i < rows.length; i++) {
      var closeTs = Date.parse(rows[i].getAttribute('data-order-close'));
      if (!isNaN(closeTs) && closeTs < now) {
        hasExpired = true;
        break;
      }
    }

    if (hasExpired) {
      checkoutBtn.setAttribute('disabled', 'disabled');
    } else {
      checkoutBtn.removeAttribute('disabled');
    }
  }


  // ─── Watch for cart mutations and re-enforce ──────────────────────────────
  //
  // Dawn's cart.js replaces innerHTML inside #main-cart-items on every
  // quantity update or removal. Observing that node (with subtree: true)
  // catches the mutation regardless of how deep the replacement lands.
  //
  // Debounced to requestAnimationFrame so a single innerHTML replacement
  // (which fires one mutation per removed + added child) collapses into a
  // single enforcement call. Correctness is not affected by multiple calls;
  // the debounce is purely a perf hygiene measure.
  function observeCartMutations() {
    var cartItems = document.getElementById('main-cart-items');
    if (!cartItems) return; // not on the cart page

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        enforceCartExpiry();
        pending = false;
      });
    });

    observer.observe(cartItems, {
      childList: true,
      subtree:   true,
    });
  }


  // ─── Init ────────────────────────────────────────────────────────────────────

  function initCartPage() {
    enforceCartExpiry();
    observeCartMutations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceDefaultWindow);
    document.addEventListener('DOMContentLoaded', initCartPage);
  } else {
    // DOMContentLoaded has already fired (script loaded with defer or at end of body).
    enforceDefaultWindow();
    initCartPage();
  }

}());
